/**
 * Product catalog types + fetch helpers. Shapes mirror api/app/schemas.py
 * (ProductListItem / ProductDetail / ProductCategoryRef) — keep in sync.
 *
 * Server Components fetch FastAPI directly via `catalogApi` (no browser hop).
 * Client components hit the `/api/catalog/*` proxy instead (see fetchProducts*).
 */
const API = process.env.API_INTERNAL_URL ?? 'http://localhost:8000'

export type ProductListItem = {
  id: string
  slug: string
  name_fa: string
  name_en: string | null
  tagline_fa: string | null
  unit_fa: string | null
  price_toman: number | null
  image_url: string | null
  in_stock: boolean
  status: 'draft' | 'published'
  category_id: string | null
  sort: number
}

export type ProductDetail = ProductListItem & {
  description_fa: string | null
  image_key: string | null
  created_at: string
  updated_at: string
}

export type ProductList = {
  items: ProductListItem[]
  total: number
  limit: number
  offset: number
}

export type ProductCategoryRef = {
  id: string
  slug: string
  name_fa: string
  name_en: string | null
  parent_id: string | null
  sort: number
}

/** Server-side direct fetch to FastAPI (Server Components only). */
async function catalogApi<T>(path: string): Promise<T | null> {
  try {
    const res = await fetch(`${API}${path}`, { cache: 'no-store' })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    return null
  }
}

/**
 * slug → price_toman map for the homepage cards. Cached 5 min in the Next
 * data cache — the homepage is request-rendered anyway (the root layout reads
 * cookies), so fetch-level revalidate is what actually caps API traffic;
 * `catalogApi`'s no-store stays right for the force-dynamic /products pages.
 * API down (incl. the firewalled Liara build runner) → {} and the cards
 * simply render without prices.
 */
export async function getHomePriceMap(): Promise<Record<string, number | null>> {
  try {
    const res = await fetch(`${API}/products`, { next: { revalidate: 300 } })
    if (!res.ok) return {}
    const data = (await res.json()) as ProductList
    return Object.fromEntries(data.items.map((p) => [p.slug, p.price_toman]))
  } catch {
    return {}
  }
}

export async function getProducts(categorySlug?: string): Promise<ProductListItem[]> {
  const q = categorySlug ? `?category=${encodeURIComponent(categorySlug)}` : ''
  const data = await catalogApi<ProductList>(`/products${q}`)
  return data?.items ?? []
}

export async function getProduct(slug: string): Promise<ProductDetail | null> {
  return catalogApi<ProductDetail>(`/products/${encodeURIComponent(slug)}`)
}

/**
 * All published product slugs, paging past the API's default 60-row cap
 * (limit max is 200). For the sitemap — a backend hiccup returns what we
 * have so far, mirroring fetchPublishedPosts in app/sitemap.ts.
 */
export async function getAllProductSlugs(): Promise<string[]> {
  const PAGE_SIZE = 200
  const out: string[] = []
  let offset = 0
  while (true) {
    const data = await catalogApi<ProductList>(
      `/products?limit=${PAGE_SIZE}&offset=${offset}`,
    )
    if (!data || data.items.length === 0) break
    out.push(...data.items.map((p) => p.slug))
    if (offset + data.items.length >= data.total) break
    offset += data.items.length
  }
  return out
}

/** Whole-toman formatting in Persian digits, e.g. 320000 → «۳۲۰٬۰۰۰ تومان». */
export function formatToman(value: number | null): string {
  if (value === null || value === undefined) return 'استعلام قیمت'
  const grouped = value.toLocaleString('en-US').replace(/,/g, '٬')
  const fa = grouped.replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)])
  return `${fa} تومان`
}
